/**
 * resultLabelKind — classify a roll result into the stinger family the
 * showdown label plays when it pops (DICE_RESULT_WIN / _POINT / _LOSS).
 *
 * Shared by the two label-producing sites (v2 State._checkPlayers and
 * GameFlowController.handleNewRollResult) so the sound always agrees with
 * the label text. The kind is the roll's INTRINSIC outcome (instant win /
 * point set / instant loss), not the banker-vs-shooter settlement — a
 * cee-lo PAIR is always 'point' even if it later beats the banker.
 */

import { CeeloResultType } from '@sdl/shared';
import type { CeeloResult, ComeOutOutcome, PointOutcome } from '@sdl/shared';

export type ResultLabelKind = 'win' | 'point' | 'loss';

export function ceeloLabelKind(r: CeeloResult): ResultLabelKind | null {
  switch (r.type) {
    case CeeloResultType.CEELO:
    case CeeloResultType.TRIPS:
    case CeeloResultType.TRIPS_6:
      return 'win';
    case CeeloResultType.PAIR:
      return 'point';
    case CeeloResultType.ACE:
      return 'loss';
    default:
      return null; // NOTHING — filtered before any label shows
  }
}

/**
 * Cee-lo SHOOTER kind — relative to the banker's roll, which is what the
 * shooter actually wins or loses on. Ranks are totally ordered (ACE 1 …
 * CEELO 14), so this also covers instant rolls: shooter's CEE-LO outranks
 * everything but an equal CEE-LO (push), an ACE loses to everything but an
 * equal ACE. Rank tie → 'point' (neutral; the tie chip carries the beat).
 */
export function ceeloShooterVsBankerKind(shooter: CeeloResult, banker: CeeloResult): ResultLabelKind {
  if (shooter.rank > banker.rank) return 'win';
  if (shooter.rank < banker.rank) return 'loss';
  return 'point';
}

export function crapsComeOutLabelKind(o: ComeOutOutcome): ResultLabelKind | null {
  switch (o.kind) {
    case 'natural': return 'win';
    case 'craps': return 'loss';
    case 'point': return 'point';
    default: return null;
  }
}

export function crapsPointLabelKind(o: PointOutcome): ResultLabelKind | null {
  switch (o.kind) {
    case 'point_hit': return 'win';
    case 'seven_out': return 'loss';
    default: return null; // continue — no label, no stinger
  }
}
